import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button, Modal, Slider } from 'antd';
import styles from './LogoCropModal.module.css';

export interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LogoCropModalProps {
  open: boolean;
  imageSrc: string;
  onCancel: () => void;
  onConfirm: (croppedAreaPixels: CroppedAreaPixels) => void;
  isLoading?: boolean;
  /** the frame the logo is cropped for; both crop the same square (aspect 1) */
  shape?: 'circle' | 'square';
}

const LogoCropModal: React.FC<LogoCropModalProps> = ({
  open,
  imageSrc,
  onCancel,
  onConfirm,
  isLoading = false,
  shape = 'circle',
}) => {
  const isSquare = shape === 'square';
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  const onCropComplete = useCallback(
    (_croppedArea: CroppedAreaPixels, croppedAreaPixels: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleConfirm = () => {
    if (croppedAreaPixels) {
      onConfirm(croppedAreaPixels);
    }
  };

  return (
    <Modal
      centered
      className="modal-custom-styles"
      open={open}
      title={isSquare ? 'Crop logo for square display' : 'Crop logo for circular display'}
      onCancel={onCancel}
      width={500}
      footer={
        <div className={styles.footer}>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleConfirm} loading={isLoading}>
            Apply
          </Button>
        </div>
      }
      destroyOnClose
    >
      <div className={styles.container}>
        <div className={styles.cropContainer}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.1}
            aspect={1}
            cropShape={isSquare ? 'rect' : 'round'}
            showGrid={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            objectFit="contain"
            restrictPosition={false}
          />
        </div>
        <div className={styles.controls}>
          <span className={styles.zoomLabel}>Zoom</span>
          <Slider
            value={zoom}
            min={0.1}
            max={3}
            step={0.05}
            onChange={(value) => setZoom(value)}
            className={styles.slider}
          />
        </div>
        <p className={styles.hint}>
          Move and zoom the image to select the area that will be shown in{' '}
          {isSquare ? 'square' : 'circular'} elements.
        </p>
      </div>
    </Modal>
  );
};

export default LogoCropModal;
